import { createContext, useContext, useEffect } from 'react';
import { matchPath, useLocation, useParams } from 'react-router';
import qs from 'query-string';

import { VehicleLookupContext } from 'Context/vehicleLookup';
import { VehicleContext } from 'Context/vehicles';

import { APIResponseStatus } from 'Services/api.types';

import { SaleConfirmationSentryOpts } from '../../containers/app/documentCapture/DocumentCapture.helpers';

interface UrlFallbackContextType {
  useUrlFallback?: boolean;
}

export const UrlFallbackContext = createContext({});

type UseUrlVRM = {
  initialStatus: APIResponseStatus;
  valuationRef?: number | null | string;
  vrm?: string;
}

const useUrlVRM = ({ optimisticReturn = false } = {}): UseUrlVRM => {
  const { useUrlFallback } = useContext<UrlFallbackContextType>(UrlFallbackContext) ?? {};
  const { vehicleState: vehicles } = useContext(VehicleContext);
  const { vehicleLookupActions, vehicleLookupState: currentLookupVehicle } = useContext(VehicleLookupContext);

  const { vrm: vrmFromParams } = useParams<{ vrm: string }>();
  const { pathname, search } = useLocation();

  let vrmFallback: string;
  /**
   * useParams does not work on Doc Capture routes directly because they are absolute URLS
   * declared in `DocumentCaptureView`.
   * We need to manually declare a pattern here instead to get the VRM for these situations
   * to use as a fallback VRM to ensure this is not undefined for useMotorwayOffer hook
   */
  if (useUrlFallback) {
    const match = matchPath(pathname, {
      exact: true,
      path: '/:vrm/*',
      strict: false,
    });

    vrmFallback = (match?.params as { vrm: string })?.vrm;
  }

  const vehicle = vehicles.find(({ vrm: v }) => (v === (vrmFromParams ?? vrmFallback)));

  const { enquiry: { dpEnquiryId } = { dpEnquiryId: null }, vrm } = vehicle || {};
  const { vrm: currentVrm } = currentLookupVehicle || {};

  let { valuation_ref: valuationRef = dpEnquiryId } = qs.parse(search);

  if (valuationRef) {
    valuationRef = parseInt(valuationRef.toString());
  }

  if (Number.isNaN(valuationRef)) {
    valuationRef = null;
  }

  // Make sure we display info for the VRM presented in the URL
  // if not, this useEffect will try to change this
  useEffect(() => {
    // Looks like the VRM in the url doesn't match any of the vehicles in the account
    if (!vrm) {
      window?.Sentry?.captureException?.(new Error('Vehicle not found in the account vehicles'), {
        ...SaleConfirmationSentryOpts,
        extra: {
          requestedVRM: vrmFromParams,
          search,
          valuationRef,
        },
        level: 'warning',
      });

      return;
    }

    // when dpEnquiryId of current vehicle does not match valuationRef from URL
    if (dpEnquiryId !== valuationRef) {
      // map all vehicles vrm and dpEnquiryId for extra logging
      const vehiclesEnquiryVRMs = vehicles.map((v) => ({
        dpEnquiryId: v.enquiry?.dpEnquiryId,
        vrm: v.vrm,
      }));

      window?.Sentry?.captureException?.(new Error('dpEnquiryId did not match valuation_ref from URL'), {
        ...SaleConfirmationSentryOpts,
        extra: {
          dpEnquiryId,
          requestedVRM: vrmFromParams,
          search,
          valuationRef,
          vehiclesEnquiryVRMs,
        },
        level: 'info',
      });
    }

    // vehicle in lookup is not the one which is requested in the URL, lets update the context
    if (currentVrm !== vrm) {
      window?.Sentry?.captureException?.(new Error('VRM in URL did not match current lookup vehicle'), {
        ...SaleConfirmationSentryOpts,
        extra: {
          requestedVRM: vrm,
          search,
          valuationRef,
          vrmInLookup: currentVrm,
        },
        level: 'info',
      });

      vehicleLookupActions.add({ vehicle });
    }
  }, [currentVrm, dpEnquiryId, search, valuationRef, vehicle, vehicleLookupActions, vrm, vrmFromParams]);

  if (!optimisticReturn && !vrm) {
    return {
      initialStatus: APIResponseStatus.ERROR,
    };
  }

  const optimisticVRMFromParams = (optimisticReturn && vrmFromParams) || undefined;

  return {
    initialStatus: APIResponseStatus.LOADING,
    valuationRef,
    vrm: vrm ?? optimisticVRMFromParams,
  };
};

export default useUrlVRM;
